import { useState } from "react";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, feeOf, fmtBtc, fmtUsd } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const SellCard = ({ price, onDone }) => {
  const { user, setUser } = useAuth();
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const bal = user?.btc_balance || 0;
  const amt = parseFloat(val) || 0;

  const sell = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/trade/sell", { btc_amount: amt });
      setUser(data.user);
      toast.success(`Sold ${fmtBtc(amt)} BTC — ${fmtUsd(data.transaction.usd_amount)} credited`);
      setVal("");
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="sell-card">
      <div>
        <div className="mb-2 flex justify-between text-xs">
          <span className="font-semibold uppercase tracking-[0.2em] text-[#64748B]">You sell (BTC)</span>
          <span className="font-mono text-[#94A3B8]" data-testid="sell-available-balance">Avail: {fmtBtc(bal)}</span>
        </div>
        <input value={val} onChange={(e) => setVal(e.target.value)} type="number" step="0.00000001" min="0" placeholder="0.00000000"
          data-testid="sell-btc-input"
          className="w-full rounded-xl border border-[#212638] bg-[#0F111A] px-4 py-3 font-mono text-lg outline-none transition-colors focus:border-[#F7931A]" />
        <div className="mt-2 grid grid-cols-4 gap-2">
          {[25, 50, 75, 100].map((p) => (
            <button key={p} onClick={() => setVal(((bal * p) / 100).toFixed(8))} data-testid={`sell-pct-${p}`}
              className="rounded-lg border border-[#212638] py-1.5 font-mono text-xs text-[#94A3B8] transition-colors hover:bg-[#1C2030] hover:text-white">{p}%</button>
          ))}
        </div>
      </div>
      <div className="rounded-xl border border-[#212638] bg-[#0F111A] p-4 text-sm">
        <div className="flex justify-between"><span className="text-[#94A3B8]">Gross value</span><span className="font-mono">{fmtUsd(price ? amt * price : 0)}</span></div>
        <div className="mt-2 flex justify-between"><span className="text-[#94A3B8]">Fee (1%, min $0.01)</span><span className="font-mono" data-testid="sell-fee-preview">{fmtUsd(price ? feeOf(amt * price) : 0)}</span></div>
        <div className="mt-2 flex justify-between"><span className="text-[#94A3B8]">You receive (USD)</span><span className="font-mono font-semibold text-emerald-400" data-testid="sell-usd-preview">{fmtUsd(price ? Math.max(0, Math.round(amt * price * 100) / 100 - feeOf(Math.round(amt * price * 100) / 100)) : 0)}</span></div>
        <div className="mt-2 flex justify-between"><span className="text-[#94A3B8]">Payout</span><span>Instant to USD balance</span></div>
      </div>
      <button onClick={sell} disabled={busy || amt <= 0 || amt > bal + 1e-12 || !price} data-testid="sell-submit-button"
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-500 py-3.5 font-semibold text-white transition-colors hover:bg-red-400 disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={18} /> : <ArrowUpRight size={18} />} Sell BTC
      </button>
      {bal === 0 && <p className="text-center text-xs text-[#64748B]">Buy some BTC with your card first to sell.</p>}
    </div>
  );
};
